import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import VoucherFormDrawer from "@/components/VoucherFormDrawer";
import BankDepositDialog from "@/components/BankDepositDialog";
import CashbookExportDialog from "@/components/CashbookExportDialog";
import MultiBranchCashbookDialog from "@/components/MultiBranchCashbookDialog";
import { fmtMoney, fmtDate } from "@/lib/format";
import {
  ArrowsDownUp,
  ArrowDown,
  ArrowUp,
  Printer,
  MagnifyingGlass,
  Trash,
  BookOpen,
  GridFour,
  Bank,
  Check,
  X as XIcon,
  Paperclip,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const KIND_BADGE = {
  receipt: {
    icon: ArrowDown,
    label: "RECEIPT",
    className: "border-[#00B341] bg-[#00B341]/10 text-[#00B341]",
  },
  payment: {
    icon: ArrowUp,
    label: "PAYMENT",
    className: "border-destructive bg-destructive/10 text-destructive",
  },
};

function KindBadge({ kind }) {
  const meta = KIND_BADGE[kind] || KIND_BADGE.receipt;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${meta.className}`}
      data-testid={`voucher-kind-badge-${kind}`}
    >
      <Icon size={10} weight="bold" />
      {meta.label}
    </span>
  );
}

function StatPill({ label, value, accent, testId }) {
  return (
    <div className={`border p-4 ${accent}`} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">
        {label}
      </div>
      <div className="font-display text-2xl font-bold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonthISO() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function printVoucher(v) {
  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) return;
  const esc = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
  const isReceipt = v.kind === "receipt";
  const tone = isReceipt ? "#00B341" : "#FF2A2A";
  const lines = v.payment_lines && v.payment_lines.length
    ? v.payment_lines
    : [
        {
          method: v.payment_method || "cash",
          amount: v.amount || 0,
          reference: v.reference || "",
        },
      ];
  const linesRows = lines
    .map(
      (ln) => `<tr>
        <td style="text-transform:uppercase;font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(ln.method)}</td>
        <td style="font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(ln.reference || "—")}</td>
        <td style="text-align:right;font-weight:600">${esc(fmtMoney(ln.amount))}</td>
      </tr>`,
    )
    .join("");
  w.document.write(`<!doctype html><html><head><title>${esc(v.voucher_no)}</title>
  <style>
    *{box-sizing:border-box}
    body{font-family:'Work Sans','Helvetica',sans-serif;color:#0A0A0A;margin:32px;background:#fff}
    h1{font-family:'Cabinet Grotesk','Helvetica',sans-serif;font-size:22px;margin:0}
    .eyebrow{font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.3em;color:#5C5C5C}
    .tag{display:inline-block;padding:2px 8px;border:1px solid ${tone};color:${tone};font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.15em;margin-bottom:8px}
    .card{border:1px solid #0A0A0A;padding:16px;margin-top:16px}
    .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #ddd;font-size:13px}
    .row .l{color:#5C5C5C}
    .row:last-child{border-bottom:0}
    table{width:100%;border-collapse:collapse;margin-top:8px}
    th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #eee;font-size:12px}
    th{font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#5C5C5C;border-bottom:1px solid #0A0A0A}
    .totals{margin-top:14px;border-top:2px solid #0A0A0A;padding-top:10px}
    .amount{font-family:'Cabinet Grotesk',sans-serif;font-size:28px;font-weight:700;color:${tone}}
    .narration{font-size:13px;white-space:pre-wrap;color:#333}
    .foot{margin-top:32px;display:flex;justify-content:space-between;font-size:11px;font-family:'IBM Plex Mono',monospace;color:#5C5C5C;text-transform:uppercase;letter-spacing:.15em}
    @media print{button{display:none}}
  </style></head><body>
  <div class="tag">${isReceipt ? "Receipt Voucher" : "Payment Voucher"}</div>
  <h1>${esc(v.voucher_no)}</h1>
  <div class="eyebrow">Date · ${esc(fmtDate(v.date))} · Branch ${esc(v.branch_id)}</div>
  <div class="card">
    <div class="row"><span class="l">${isReceipt ? "Received From" : "Paid To"}</span><span><b>${esc(v.party_name)}</b> <span style="color:#5C5C5C">(${esc(v.party_type)})</span></span></div>
    <table>
      <thead><tr><th>Method</th><th>Reference</th><th style="text-align:right">Amount (₹)</th></tr></thead>
      <tbody>${linesRows}</tbody>
    </table>
    <div class="totals">
      <div style="font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#5C5C5C">Total</div>
      <div class="amount">${esc(fmtMoney(v.amount))}</div>
    </div>
  </div>
  ${v.narration ? `<div class="card"><div class="eyebrow" style="margin-bottom:6px">Narration</div><div class="narration">${esc(v.narration)}</div></div>` : ""}
  <div class="foot"><span>Created by ${esc(v.created_by_name || "—")}</span><span>FY ${esc(v.fy || "")}</span></div>
  <script>window.onload=function(){window.print();}</script>
  </body></html>`);
  w.document.close();
}

export default function VouchersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [data, setData] = useState({
    rows: [],
    totals: { receipts: 0, payments: 0, net: 0, count: 0 },
  });
  const [kindFilter, setKindFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [defaultKind, setDefaultKind] = useState("receipt");
  const [showCashbook, setShowCashbook] = useState(false);
  const [showMultiBranch, setShowMultiBranch] = useState(false);
  const [showBankDeposit, setShowBankDeposit] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (kindFilter !== "all") params.kind = kindFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search.trim()) params.q = search.trim();
      const { data: res } = await api.get("/vouchers", { params });
      setData(res || { rows: [], totals: { receipts: 0, payments: 0, net: 0, count: 0 } });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (kindFilter !== "all") params.kind = kindFilter;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (search.trim()) params.q = search.trim();
        const { data: res } = await api.get("/vouchers", { params });
        if (!cancelled) {
          setData(res || { rows: [], totals: { receipts: 0, payments: 0, net: 0, count: 0 } });
        }
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter, dateFrom, dateTo]);

  const handleDelete = async (vid, vno) => {
    if (!window.confirm(`Delete voucher ${vno}? This cannot be undone.`)) return;
    try {
      await api.delete(`/vouchers/${vid}`);
      toast.success("Voucher deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleViewEvidence = (v) => {
    const files = v.evidence_files || [];
    if (files.length === 0) {
      toast.info("No evidence attached to this voucher.");
      return;
    }
    // Open each evidence file in a new tab. The download endpoint streams the
    // file inline (Content-Disposition: inline) so images/PDFs render in
    // the browser. Auth is via the same JWT cookie/header — the API client
    // sets the bearer token globally.
    const base = api.defaults.baseURL;
    const token = api.defaults.headers?.common?.Authorization;
    for (const f of files) {
      const url = `${base}/vouchers/${v.id}/evidence/${f.id}`;
      // Bearer tokens can't be sent through a plain window.open, so we
      // fetch as a blob and open the resulting object URL — this preserves
      // auth without exposing the token in the URL bar.
      fetch(url, { headers: token ? { Authorization: token } : {} })
        .then((r) => r.blob())
        .then((blob) => {
          const obj = URL.createObjectURL(blob);
          window.open(obj, "_blank", "noopener");
        })
        .catch((err) => toast.error(`Could not load ${f.filename}: ${err.message || err}`));
    }
  };

  const canApprove = isAdmin || (user?.permissions || []).includes("vouchers.approve");

  const handleApprove = async (vid) => {
    try {
      await api.post(`/vouchers/${vid}/approve`);
      toast.success("Voucher approved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const handleReject = async (vid) => {
    const reason = window.prompt("Reason for rejection (will be logged):", "");
    if (reason === null) return;
    try {
      await api.post(`/vouchers/${vid}/reject`, { reason });
      toast.success("Voucher rejected — reversed from cashbook");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const handleUploadEvidence = async (vid, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vouchers/${vid}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openNew = (kind) => {
    setDefaultKind(kind);
    setShowForm(true);
  };

  const netClass = useMemo(() => {
    const n = data.totals.net;
    if (n > 0) return "border-[#00B341] bg-[#00B341]/5 text-ink";
    if (n < 0) return "border-destructive bg-destructive/5 text-ink";
    return "border-canvas-line bg-canvas-card text-ink";
  }, [data.totals.net]);

  return (
    <div data-testid="vouchers-page">
      <PageHeader
        eyebrow="07 / Cashbook"
        title="Receipts & Vouchers"
        subtitle="Misc money-in (receipts) and money-out (payments) entries. Auto-numbered per branch + FY."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMultiBranch(true)}
              data-testid="open-multi-branch-cashbook"
              className="px-3 py-2 border border-ink text-ink text-sm font-semibold hover:bg-ink hover:text-white transition-colors flex items-center gap-1.5"
            >
              <GridFour size={14} weight="bold" />
              Multi-Branch
            </button>
            <button
              onClick={() => setShowCashbook(true)}
              data-testid="open-cashbook-export"
              className="px-3 py-2 border border-ink text-ink text-sm font-semibold hover:bg-ink hover:text-white transition-colors flex items-center gap-1.5"
            >
              <BookOpen size={14} weight="bold" />
              Cashbook
            </button>
            <button
              onClick={() => setShowBankDeposit(true)}
              data-testid="new-bank-deposit"
              className="px-3 py-2 border border-[#0EA5E9] text-[#0EA5E9] text-sm font-semibold hover:bg-[#0EA5E9] hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Bank size={14} weight="bold" />
              Bank Deposit
            </button>
            <button
              onClick={() => openNew("receipt")}
              data-testid="new-receipt-voucher"
              className="px-3 py-2 border border-[#00B341] text-[#00B341] text-sm font-semibold hover:bg-[#00B341] hover:text-white transition-colors flex items-center gap-1.5"
            >
              <ArrowDown size={14} weight="bold" />
              New Receipt
            </button>
            <button
              onClick={() => openNew("payment")}
              data-testid="new-payment-voucher"
              className="px-3 py-2 border border-destructive text-destructive text-sm font-semibold hover:bg-destructive hover:text-white transition-colors flex items-center gap-1.5"
            >
              <ArrowUp size={14} weight="bold" />
              New Payment
            </button>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill
            label="Vouchers in range"
            value={data.totals.count}
            accent="border-canvas-line bg-canvas-card text-ink"
            testId="voucher-stat-count"
          />
          <StatPill
            label="Receipts (in)"
            value={fmtMoney(data.totals.receipts)}
            accent="border-[#00B341] bg-[#00B341]/5 text-ink"
            testId="voucher-stat-receipts"
          />
          <StatPill
            label="Payments (out)"
            value={fmtMoney(data.totals.payments)}
            accent="border-destructive bg-destructive/5 text-ink"
            testId="voucher-stat-payments"
          />
          <StatPill
            label="Net"
            value={fmtMoney(data.totals.net)}
            accent={netClass}
            testId="voucher-stat-net"
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex border border-canvas-line">
            {[
              { v: "all", l: "All", icon: ArrowsDownUp },
              { v: "receipt", l: "Receipts", icon: ArrowDown },
              { v: "payment", l: "Payments", icon: ArrowUp },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = kindFilter === opt.v;
              return (
                <button
                  key={opt.v}
                  onClick={() => setKindFilter(opt.v)}
                  data-testid={`voucher-filter-${opt.v}`}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-1.5 ${
                    active ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"
                  }`}
                >
                  <Icon size={11} weight="bold" />
                  {opt.l}
                </button>
              );
            })}
          </div>

          <div className="flex items-end gap-2">
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="voucher-date-from"
                className="px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="voucher-date-to"
                className="px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
              />
            </div>
          </div>

          <div className="flex-1 min-w-[200px] relative">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Search
            </label>
            <MagnifyingGlass
              size={12}
              weight="bold"
              className="absolute left-2 top-[28px] text-ink-subtle"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Voucher no, party, narration, reference"
              data-testid="voucher-search"
              className="w-full pl-7 pr-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
            />
          </div>
          <button
            onClick={load}
            data-testid="voucher-search-apply"
            className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand"
          >
            Apply
          </button>
        </div>

        <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
          <table className="w-full text-sm" data-testid="vouchers-table">
            <thead className="border-b border-canvas-line bg-canvas-panel">
              <tr>
                {[
                  "Date",
                  "Voucher No",
                  "Kind",
                  "Party",
                  "Method",
                  "Reference",
                  "Amount ₹",
                  "Narration",
                  "Status",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-10 text-center font-mono text-xs text-ink-subtle"
                  >
                    No vouchers in this window.
                  </td>
                </tr>
              )}
              {data.rows.map((v) => {
                const lines = (v.payment_lines && v.payment_lines.length)
                  ? v.payment_lines
                  : [
                      {
                        method: v.payment_method || "cash",
                        amount: v.amount,
                        reference: v.reference || "",
                      },
                    ];
                const isMixed = lines.length > 1;
                return (
                <tr
                  key={v.id}
                  data-testid={`voucher-row-${v.id}`}
                  className="border-b border-canvas-line last:border-b-0 hover:bg-canvas-panel/40"
                >
                  <td className="px-3 py-3 font-mono text-[11px]">{fmtDate(v.date)}</td>
                  <td className="px-3 py-3 font-mono text-[11px] font-semibold">
                    {v.voucher_no}
                  </td>
                  <td className="px-3 py-3">
                    <KindBadge kind={v.kind} />
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-semibold text-ink">{v.party_name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
                      {v.party_type}
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-[10px] uppercase tracking-wider">
                    {isMixed ? (
                      <div className="space-y-0.5">
                        {lines.map((ln, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <span className="border border-canvas-line px-1 py-px">
                              {ln.method}
                            </span>
                            <span className="text-ink-subtle">{fmtMoney(ln.amount)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      lines[0].method
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-[11px] text-ink-muted">
                    {isMixed ? (
                      <div className="space-y-0.5">
                        {lines.map((ln, i) => (
                          <div key={i}>{ln.reference || "—"}</div>
                        ))}
                      </div>
                    ) : (
                      lines[0].reference || "—"
                    )}
                  </td>
                  <td
                    className={`px-3 py-3 text-right font-mono tabular-nums font-semibold ${
                      v.kind === "receipt" ? "text-[#00B341]" : "text-destructive"
                    }`}
                  >
                    {v.kind === "receipt" ? "+" : "−"} {fmtMoney(v.amount)}
                  </td>
                  <td className="px-3 py-3 text-xs text-ink-muted max-w-[280px] truncate">
                    {v.narration || "—"}
                  </td>
                  <td className="px-3 py-3">
                    {v.approval_status === "pending" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#E08600] bg-[#FFF6E5] text-[#A35700] text-[9px] font-mono uppercase tracking-wider">
                        PENDING
                        {(v.evidence_files || []).length === 0 && <span title="No evidence uploaded">·NO-EVD</span>}
                      </span>
                    )}
                    {v.approval_status === "approved" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#00713a] bg-[#00B341]/10 text-[#00713a] text-[9px] font-mono uppercase tracking-wider" title={`Approved by ${v.approved_by_name || "—"}`}>
                        APPROVED
                      </span>
                    )}
                    {v.approval_status === "rejected" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-destructive bg-destructive/10 text-destructive text-[9px] font-mono uppercase tracking-wider" title={v.rejection_reason || "Rejected"}>
                        REJECTED
                      </span>
                    )}
                    {(v.approval_status === "not_required" || !v.approval_status) && (
                      <span className="text-[10px] font-mono text-ink-subtle">—</span>
                    )}
                    {(v.evidence_files || []).length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleViewEvidence(v)}
                        data-testid={`view-evidence-${v.id}`}
                        className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-mono text-ink-muted hover:text-ink hover:underline cursor-pointer"
                        title={`Open ${v.evidence_files.length} evidence file(s)`}
                      >
                        <Paperclip size={9} weight="bold" />{v.evidence_files.length}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {v.approval_status === "pending" && (
                        <>
                          {(v.evidence_files || []).length > 0 && (
                            <button
                              onClick={() => handleViewEvidence(v)}
                              data-testid={`view-evidence-btn-${v.id}`}
                              className="p-1.5 border border-canvas-line hover:border-ink"
                              title={`View ${v.evidence_files.length} evidence file(s) before approving`}
                            >
                              <Paperclip size={12} weight="bold" />
                            </button>
                          )}
                          <label
                            data-testid={`upload-evidence-${v.id}`}
                            className="p-1.5 border border-canvas-line hover:border-ink cursor-pointer inline-flex"
                            title="Upload more evidence (image/PDF)"
                          >
                            <Paperclip size={12} weight="bold" />
                            <span className="ml-0.5 text-[8px] font-bold">+</span>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              className="hidden"
                              onChange={(e) => {
                                handleUploadEvidence(v.id, e.target.files?.[0]);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          {canApprove && (
                            <>
                              <button
                                onClick={() => handleApprove(v.id)}
                                data-testid={`approve-voucher-${v.id}`}
                                className="p-1.5 border border-[#00713a] text-[#00713a] hover:bg-[#00B341]/10"
                                title="Approve voucher"
                              >
                                <Check size={12} weight="bold" />
                              </button>
                              <button
                                onClick={() => handleReject(v.id)}
                                data-testid={`reject-voucher-${v.id}`}
                                className="p-1.5 border border-destructive text-destructive hover:bg-destructive/10"
                                title="Reject voucher (reverses cashbook)"
                              >
                                <XIcon size={12} weight="bold" />
                              </button>
                            </>
                          )}
                        </>
                      )}
                      {v.approval_status === "approved" && canApprove && (
                        <button
                          onClick={() => handleReject(v.id)}
                          data-testid={`reject-voucher-${v.id}`}
                          className="p-1.5 border border-canvas-line text-ink-subtle hover:border-destructive hover:text-destructive"
                          title="Reverse approval"
                        >
                          <XIcon size={12} weight="bold" />
                        </button>
                      )}
                      <button
                        onClick={() => printVoucher(v)}
                        data-testid={`print-voucher-${v.id}`}
                        className="p-1.5 border border-canvas-line hover:border-ink"
                        title="Print voucher"
                      >
                        <Printer size={12} weight="bold" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(v.id, v.voucher_no)}
                          data-testid={`delete-voucher-${v.id}`}
                          className="p-1.5 border border-canvas-line text-ink-subtle hover:border-destructive hover:text-destructive"
                          title="Delete voucher (admin)"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <VoucherFormDrawer
          open
          defaultKind={defaultKind}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            load();
          }}
        />
      )}

      <CashbookExportDialog
        open={showCashbook}
        onClose={() => setShowCashbook(false)}
      />

      <MultiBranchCashbookDialog
        open={showMultiBranch}
        onClose={() => setShowMultiBranch(false)}
      />

      <BankDepositDialog
        open={showBankDeposit}
        onClose={() => setShowBankDeposit(false)}
        onSaved={() => {
          setShowBankDeposit(false);
          load();
        }}
      />
    </div>
  );
}
